/**
 * graphMailThunks.js
 *
 * Redux Toolkit async thunks that call graphMailService.js and dispatch
 * into the four mail slices (mailFolders, mailThreads, mailMessages, mailUi).
 *
 * These thunks are the single integration point between the Graph API and
 * Redux state.  Components should dispatch these thunks rather than calling
 * graphMailService directly.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';

import * as graph from '../../services/graphMailService';

// Slice actions
import {
  setFolderTree,
  upsertFolder,
  removeFolder,
  updateFolderCounts,
  setDeltaToken as setFolderDeltaToken,
} from '../mailFoldersSlice';

import {
  upsertThread,
  removeThread,
  markThreadRead   as markThreadReadAction,
  markThreadUnread as markThreadUnreadAction,
  setThreadFlagged as setThreadFlaggedAction,
  moveThread       as moveThreadAction,
  deleteThread     as deleteThreadAction,
  updateThreadPreview,
  clearFolderThreads,
} from '../mailThreadsSlice';

import {
  upsertMessages,
  upsertMessage,
  removeMessage,
  markMessageRead  as markMessageReadAction,
  setMessageFlag   as setMessageFlagAction,
} from '../mailMessagesSlice';

import {
  setGraphStatus,
  pushNotification,
  setSelectedFolder,
  setSelectedThread,
  commitSearchQuery,
  setSearchResults,
  setSearchError,
  addRecentQuery,
} from '../mailUiSlice';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns the Sent Items folder ID from Redux state, or null if not loaded. */
function getSentFolderId(state) {
  return Object.values(state.mailFolders.byId).find(f => f.wellKnownName === 'sentitems')?.id ?? null;
}

/** Dispatch a connectivity status change and surface a toast on errors. */
function handleApiError(dispatch, err, userMessage) {
  dispatch(setGraphStatus('degraded'));
  if (userMessage) {
    dispatch(pushNotification({
      type: 'error',
      message: userMessage,
      autoDismissMs: 5000,
    }));
  }
}

// ===========================================================================
// BOOTSTRAP — load folders then inbox threads in one shot
// ===========================================================================

/**
 * Bootstrap the mail UI: fetch all folders, select inbox, load its threads.
 *
 * Dispatch once when the mail page mounts.
 *
 * Usage:
 *   dispatch(bootstrapMail())
 */
export const bootstrapMail = createAsyncThunk(
  'graphMail/bootstrap',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      // 1. Folders ────────────────────────────────────────────────────────
      const folderData = await graph.fetchFolders();
      // folderData: { folders: [], rootIds: string[], childrenByParent: {} }
      // Backend already maps folders; rootIds is correctly computed server-side.

      dispatch(setFolderTree({
        folders:          folderData.folders,
        rootIds:          folderData.rootIds,
        childrenByParent: folderData.childrenByParent,
      }));

      dispatch(setGraphStatus('connected'));

      // 2. Select inbox ────────────────────────────────────────────────────
      const inbox = folderData.folders.find((f) => f.wellKnownName === 'inbox');
      if (inbox) {
        dispatch(setSelectedFolder({ folderId: inbox.id }));
        // EmailList will auto-fetch threads for the selected folder
      }

      return { folderCount: folderData.folders.length };
    } catch (err) {
      handleApiError(dispatch, err, 'Failed to load mailbox. Please refresh.');
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// FOLDERS
// ===========================================================================

/**
 * Fetch all mail folders and upsert them into mailFoldersSlice.
 *
 * Usage:
 *   dispatch(loadFolders())
 */
export const loadFolders = createAsyncThunk(
  'graphMail/loadFolders',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const data = await graph.fetchFolders();
      dispatch(setFolderTree({
        folders:          data.folders,
        rootIds:          data.rootIds,
        childrenByParent: data.childrenByParent,
      }));
      dispatch(setGraphStatus('connected'));
      return data.folders;
    } catch (err) {
      handleApiError(dispatch, err, 'Could not load folders.');
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Sync folder unread / total counts via Graph delta.
 *
 * Usage:
 *   dispatch(syncFolderDelta())
 */
export const syncFolderDelta = createAsyncThunk(
  'graphMail/syncFolderDelta',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const deltaToken = getState().mailFolders.deltaToken;
      const data       = await graph.fetchFolderDelta(deltaToken);

      (data.folders || []).forEach((f) => {
        const mapped = graph.mapGraphFolder(f);
        if (f['@removed']) {
          dispatch(removeFolder({ folderId: f.id }));
        } else {
          dispatch(upsertFolder(mapped));
          dispatch(updateFolderCounts({
            folderId:       f.id,
            unreadItemCount: f.unreadItemCount,
            totalItemCount:  f.totalItemCount,
          }));
        }
      });

      if (data.deltaToken) {
        dispatch(setFolderDeltaToken(data.deltaToken));
      }

      return data;
    } catch (err) {
      handleApiError(dispatch, err);
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// THREADS
// ===========================================================================

/**
 * Load the first page of threads for a folder.
 * Clears any previously loaded threads for the folder before inserting.
 *
 * Usage:
 *   dispatch(loadFolderThreads({ folderId }))
 */
export const loadFolderThreads = createAsyncThunk(
  'graphMail/loadFolderThreads',
  async ({ folderId }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(clearFolderThreads({ folderId }));

      const raw  = await graph.fetchThreadsByFolder(folderId, { top: 50 });
      // raw: { threads: Thread[], messages: MappedMessage[], cursor: string|null, hasMore: bool }
      // Backend already maps messages and builds threads — do NOT re-map.

      const messages = raw.messages;
      const threads  = raw.threads;

      // Upsert messages first so thread messageIds resolve
      dispatch(upsertMessages(messages));
      threads.forEach((t) => dispatch(upsertThread(t)));

      dispatch(setGraphStatus('connected'));

      return {
        folderId,
        threads,
        messages,
        cursor:  raw.cursor  || null,
        hasMore: raw.hasMore || false,
      };
    } catch (err) {
      handleApiError(dispatch, err, 'Failed to load messages.');
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Load the next page of threads for a folder (infinite scroll).
 *
 * Usage:
 *   dispatch(loadMoreFolderThreads({ folderId, cursor }))
 */
export const loadMoreFolderThreads = createAsyncThunk(
  'graphMail/loadMoreFolderThreads',
  async ({ folderId, cursor }, { dispatch, rejectWithValue }) => {
    try {
      const raw      = await graph.fetchThreadsByFolder(folderId, { cursor, top: 50 });
      // Backend already maps — do NOT re-map
      const messages = raw.messages;
      const threads  = raw.threads;

      dispatch(upsertMessages(messages));
      threads.forEach((t) => dispatch(upsertThread(t)));

      return {
        folderId,
        threads,
        messages,
        cursor:  raw.cursor  || null,
        hasMore: raw.hasMore || false,
      };
    } catch (err) {
      handleApiError(dispatch, err, 'Failed to load more messages.');
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// MESSAGE BODY
// ===========================================================================

/**
 * Lazy-load the full HTML body of a message.
 * No-ops if body is already loaded.
 *
 * Usage:
 *   dispatch(loadMessageBody({ messageId }))
 */
export const loadMessageBody = createAsyncThunk(
  'graphMail/loadMessageBody',
  async ({ messageId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const existing = getState().mailMessages.byId[messageId];
      if (existing?.body?.loaded) return { messageId };

      const body = await graph.fetchMessageBody(messageId);
      // body: { content, contentType, blockedImageCount }

      dispatch(upsertMessage({
        ...(getState().mailMessages.byId[messageId] || { id: messageId }),
        body:       { ...body, loaded: true, imagesUnblocked: false },
        bodyStatus: 'loaded',
      }));

      return { messageId, body };
    } catch (err) {
      handleApiError(dispatch, err);
      return rejectWithValue({ messageId, error: err.message });
    }
  }
);

/**
 * Fetch attachments for a message.
 *
 * Usage:
 *   dispatch(loadAttachments({ messageId }))
 */
export const loadAttachments = createAsyncThunk(
  'graphMail/loadAttachments',
  async ({ messageId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const existing = getState().mailMessages.byId[messageId];
      if (existing?.attachmentsLoaded) return { messageId };

      const data = await graph.fetchAttachments(messageId);

      dispatch(upsertMessage({
        ...(getState().mailMessages.byId[messageId] || { id: messageId }),
        attachments:      data.attachments,
        attachmentsLoaded: true,
      }));

      return { messageId, attachments: data.attachments };
    } catch (err) {
      handleApiError(dispatch, err);
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// READ / UNREAD
// ===========================================================================

/**
 * Mark a thread (all its messages) as read — optimistic + server sync.
 *
 * Usage:
 *   dispatch(markThreadReadThunk({ conversationId, messageIds }))
 */
export const markThreadReadThunk = createAsyncThunk(
  'graphMail/markThreadRead',
  async ({ conversationId, messageIds = [] }, { dispatch, rejectWithValue }) => {
    // Optimistic updates
    dispatch(markThreadReadAction({ conversationId }));
    messageIds.forEach((id) => dispatch(markMessageReadAction({ messageId: id })));

    try {
      await Promise.all(messageIds.map((id) => graph.markMessageRead(id, true)));
      return { conversationId };
    } catch (err) {
      // Rollback not implemented for brevity — a reload will correct state
      handleApiError(dispatch, err, 'Could not mark as read.');
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Mark a thread as unread (only the latest message).
 *
 * Usage:
 *   dispatch(markThreadUnreadThunk({ conversationId, latestMessageId }))
 */
export const markThreadUnreadThunk = createAsyncThunk(
  'graphMail/markThreadUnread',
  async ({ conversationId, latestMessageId }, { dispatch, rejectWithValue }) => {
    dispatch(markThreadUnreadAction({ conversationId }));

    try {
      await graph.markMessageRead(latestMessageId, false);
      return { conversationId };
    } catch (err) {
      handleApiError(dispatch, err, 'Could not mark as unread.');
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// FLAG
// ===========================================================================

/**
 * Toggle flag on a thread (flag the latest message on Graph).
 *
 * Usage:
 *   dispatch(toggleThreadFlagThunk({ conversationId, latestMessageId, isFlagged }))
 */
export const toggleThreadFlagThunk = createAsyncThunk(
  'graphMail/toggleFlag',
  async ({ conversationId, latestMessageId, isFlagged }, { dispatch, rejectWithValue }) => {
    const flagStatus = isFlagged ? 'flagged' : 'notFlagged';

    // Optimistic
    dispatch(setThreadFlaggedAction({ conversationId, isFlagged }));
    dispatch(setMessageFlagAction({ messageId: latestMessageId, flagStatus }));

    try {
      await graph.updateMessageFlag(latestMessageId, flagStatus);
      return { conversationId };
    } catch (err) {
      // Rollback
      dispatch(setThreadFlaggedAction({ conversationId, isFlagged: !isFlagged }));
      dispatch(setMessageFlagAction({
        messageId: latestMessageId,
        flagStatus: isFlagged ? 'notFlagged' : 'flagged',
      }));
      handleApiError(dispatch, err, 'Could not update flag.');
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// MOVE / DELETE
// ===========================================================================

/**
 * Move a thread to a destination folder (optimistic + server sync).
 *
 * Usage:
 *   dispatch(moveThreadThunk({ conversationId, messageIds, fromFolderId, toFolderId }))
 */
export const moveThreadThunk = createAsyncThunk(
  'graphMail/moveThread',
  async ({ conversationId, messageIds = [], fromFolderId, toFolderId }, { dispatch, rejectWithValue }) => {
    // Optimistic
    dispatch(moveThreadAction({ conversationId, fromFolderId, toFolderId }));

    try {
      await Promise.all(messageIds.map((id) => graph.moveMessage(id, toFolderId)));
      dispatch(pushNotification({ type: 'success', message: 'Message moved', autoDismissMs: 3000 }));
      return { conversationId };
    } catch (err) {
      // Rollback
      dispatch(moveThreadAction({ conversationId, fromFolderId: toFolderId, toFolderId: fromFolderId }));
      handleApiError(dispatch, err, 'Could not move message.');
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Delete a thread permanently (all messages).
 *
 * Usage:
 *   dispatch(deleteThreadThunk({ conversationId, messageIds, folderId }))
 */
export const deleteThreadThunk = createAsyncThunk(
  'graphMail/deleteThread',
  async ({ conversationId, messageIds = [], folderId }, { dispatch, rejectWithValue }) => {
    // Optimistic
    dispatch(deleteThreadAction({ conversationId, folderId }));

    try {
      await Promise.all(messageIds.map((id) => graph.deleteMessage(id)));
      dispatch(pushNotification({ type: 'success', message: 'Message deleted', autoDismissMs: 3000 }));
      return { conversationId };
    } catch (err) {
      handleApiError(dispatch, err, 'Could not delete message.');
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// SEND / REPLY / FORWARD
// ===========================================================================

/**
 * Send a new composed email.
 *
 * Payload shape mirrors the existing sendEmailsThunk for compatibility.
 *
 * Usage:
 *   dispatch(sendMailThunk({ to, cc, bcc, subject, bodyHtml, attachments,
 *                            importance, requestReadReceipt, sensitivity }))
 */
export const sendMailThunk = createAsyncThunk(
  'graphMail/send',
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const result = await graph.sendMessage(payload);
      dispatch(pushNotification({ type: 'success', message: 'Message sent', autoDismissMs: 3000 }));
      
      // Refresh Sent folder to show the new message
      const sentFolderId = getSentFolderId(getState());
      if (sentFolderId) dispatch(loadFolderThreads({ folderId: sentFolderId }));
      
      return result;
    } catch (err) {
      handleApiError(dispatch, err, 'Failed to send message.');
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Reply to a message.
 *
 * Usage:
 *   dispatch(replyThunk({ messageId, comment, toRecipients, attachments }))
 */
export const replyThunk = createAsyncThunk(
  'graphMail/reply',
  async ({ messageId, ...rest }, { dispatch, getState, rejectWithValue }) => {
    try {
      const result = await graph.replyToMessage(messageId, rest);
      dispatch(pushNotification({ type: 'success', message: 'Reply sent', autoDismissMs: 3000 }));
      
      // Update the thread with the new reply
      const message = getState().mailMessages.byId[messageId];
      if (message?.conversationId) {
        dispatch(updateThreadAfterSend({
          conversationId: message.conversationId,
          sentAt: new Date().toISOString(),
          fromEmail: getState().user?.value?.email || '',
          fromName: getState().user?.value?.name || '',
          bodyPreview: rest.comment || '',
        }));
      }
      
      return result;
    } catch (err) {
      handleApiError(dispatch, err, 'Failed to send reply.');
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Reply-all to a message.
 *
 * Usage:
 *   dispatch(replyAllThunk({ messageId, comment, attachments }))
 */
export const replyAllThunk = createAsyncThunk(
  'graphMail/replyAll',
  async ({ messageId, ...rest }, { dispatch, getState, rejectWithValue }) => {
    try {
      const result = await graph.replyAllToMessage(messageId, rest);
      dispatch(pushNotification({ type: 'success', message: 'Reply sent to all', autoDismissMs: 3000 }));
      
      // Update the thread with the new reply
      const message = getState().mailMessages.byId[messageId];
      if (message?.conversationId) {
        dispatch(updateThreadAfterSend({
          conversationId: message.conversationId,
          sentAt: new Date().toISOString(),
          fromEmail: getState().user?.value?.email || '',
          fromName: getState().user?.value?.name || '',
          bodyPreview: rest.comment || '',
        }));
      }
      
      return result;
    } catch (err) {
      handleApiError(dispatch, err, 'Failed to send reply all.');
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Update the thread summary after a reply/forward is sent.
 *
 * This updates only the thread metadata (preview text, latest sender,
 * timestamp, message count) — it does NOT inject any fake message into
 * byId or idsByThread.
 *
 * The actual optimistic message is already in pendingById (added by
 * addPendingMessage before the API call). It will be removed automatically
 * by the reconciliation logic in mailMessagesSlice when Graph delivers the
 * real message via delta sync or fetchConversationMessages.
 *
 * Usage:
 *   dispatch(updateThreadAfterSend({ conversationId, sentAt, fromEmail,
 *                                    fromName, bodyPreview, subject }))
 */
export const updateThreadAfterSend = ({
  conversationId, sentAt, fromEmail, fromName, bodyPreview,
}) => (dispatch, getState) => {
  if (!conversationId) return;

  const existingThread = getState().mailThreads.byId[conversationId];
  if (!existingThread) return;

  dispatch(upsertThread({
    ...existingThread,
    latestReceivedAt: sentAt,
    latestSenderId:   fromEmail,
    latestSenderName: fromName || fromEmail,
    bodyPreview:      bodyPreview || existingThread.bodyPreview,
    // messageCount and messageIds intentionally NOT updated here —
    // those are updated when the real message arrives from Graph.
  }));
};

/**
 * Forward a message.
 *
 * Usage:
 *   dispatch(forwardThunk({ messageId, comment, toRecipients, attachments }))
 */
export const forwardThunk = createAsyncThunk(
  'graphMail/forward',
  async ({ messageId, ...rest }, { dispatch, getState, rejectWithValue }) => {
    try {
      const result = await graph.forwardMessage(messageId, rest);
      dispatch(pushNotification({ type: 'success', message: 'Message forwarded', autoDismissMs: 3000 }));
      
      // Update the thread with the new forward
      const message = getState().mailMessages.byId[messageId];
      if (message?.conversationId) {
        dispatch(updateThreadAfterSend({
          conversationId: message.conversationId,
          sentAt: new Date().toISOString(),
          fromEmail: getState().user?.value?.email || '',
          fromName: getState().user?.value?.name || '',
          bodyPreview: rest.comment || '',
        }));
      }
      
      return result;
    } catch (err) {
      handleApiError(dispatch, err, 'Failed to forward message.');
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// DELTA SYNC POLLING
// ===========================================================================

// Per-folder delta token store (module-level — not in Redux, changes too fast)
// Persisted to localStorage so delta sync survives page reloads.
const LS_DELTA_KEY = 'bpa_folderDeltaTokens';

const folderDeltaTokens = (() => {
  try { return JSON.parse(localStorage.getItem(LS_DELTA_KEY) || '{}'); } catch { return {}; }
})();

function _persistDeltaTokens() {
  try { localStorage.setItem(LS_DELTA_KEY, JSON.stringify(folderDeltaTokens)); } catch {}
}

/**
 * Poll a single folder for new/updated/deleted messages via Graph delta.
 * Dispatches minimal Redux updates so the UI reflects changes in real time.
 *
 * Designed to be called on a setInterval (e.g. every 30 seconds).
 *
 * Usage:
 *   dispatch(pollFolderDelta({ folderId }))
 */
export const pollFolderDelta = createAsyncThunk(
  'graphMail/pollFolderDelta',
  async ({ folderId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const token  = folderDeltaTokens[folderId] || null;
      const data   = await graph.fetchMessageDelta(folderId, token);

      // Store updated delta token (persist across reloads)
      if (data.deltaToken) {
        folderDeltaTokens[folderId] = data.deltaToken;
        _persistDeltaTokens();
      }

      // Added / Modified
      // data.added / data.modified are already mapped by the backend route —
      // do NOT call mapGraphMessage here (it expects raw Graph field names and
      // would overwrite from/folderId with empty strings on already-mapped data).
      const newMessages = (data.added || []).concat(data.modified || []);

      if (newMessages.length > 0) {
        dispatch(upsertMessages(newMessages));

        // Re-build threads for affected conversationIds
        const affectedConvIds = [...new Set(newMessages.map((m) => m.conversationId))];
        affectedConvIds.forEach((cid) => {
          const allMsgs = Object.values(getState().mailMessages.byId)
            .filter((m) => m.conversationId === cid);

          if (allMsgs.length > 0) {
            const { threads } = graph.groupMessagesIntoThreads(allMsgs, folderId);
            threads.forEach((t) => dispatch(upsertThread(t)));
          }
        });

        // Notify on new messages
        if (data.added?.length > 0) {
          dispatch(pushNotification({
            type: 'info',
            message: `${data.added.length} new message${data.added.length > 1 ? 's' : ''}`,
            autoDismissMs: 5000,
          }));
        }
      }

      // Removed
      (data.removed || []).forEach(({ id }) => {
        const msg = getState().mailMessages.byId[id];
        if (msg) {
          dispatch(removeMessage({ messageId: id, conversationId: msg.conversationId }));
          // If thread is now empty, remove it
          const remaining = getState().mailMessages.idsByThread[msg.conversationId] || [];
          if (remaining.length === 0) {
            dispatch(removeThread({ conversationId: msg.conversationId, folderId }));
          }
        }
      });

      // Folder unread badge: increment for genuinely new unread messages only.
      // (modified/removed messages don't change total count here — delta handles that)
      const trulyNewUnread = (data.added || []).filter(m => !m.isRead);
      if (trulyNewUnread.length > 0) {
        const folderState = getState().mailFolders.byId[folderId];
        if (folderState) {
          dispatch(updateFolderCounts({
            folderId,
            unreadItemCount: (folderState.unreadItemCount || 0) + trulyNewUnread.length,
            totalItemCount:  (folderState.totalItemCount  || 0) + (data.added?.length || 0),
          }));
        }
      }

      return { folderId, added: data.added?.length || 0 };
    } catch (err) {
      handleApiError(dispatch, err);
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// CONVENIENCE: select a folder and load its threads
// ===========================================================================

/**
 * Select a folder in the UI and immediately load its threads if not cached.
 *
 * Usage:
 *   dispatch(selectFolderAndLoad({ folderId }))
 */
export const selectFolderAndLoad = createAsyncThunk(
  'graphMail/selectFolderAndLoad',
  async ({ folderId }, { dispatch }) => {
    // Only update UI selection — EmailList.jsx handles thread fetching
    // via its own fetchThreadsByFolder thunk (correct mapping, no double-map).
    dispatch(setSelectedFolder({ folderId }));
  }
);

// ===========================================================================
// CONVERSATION: load ALL messages across all folders
// ===========================================================================

/**
 * Fetch every message in a conversation regardless of which folder it lives in.
 * Dispatched by ReadingPane whenever a thread is selected so the thread view
 * always shows the full history (e.g. Sent Items + Inbox messages together).
 *
 * Usage:
 *   dispatch(fetchConversationMessages({ conversationId }))
 */
export const fetchConversationMessages = createAsyncThunk(
  'graphMail/fetchConversationMessages',
  async ({ conversationId }, { dispatch, getState, rejectWithValue }) => {
    if (!conversationId) return;
    try {
      const data = await graph.fetchConversationMessages(conversationId);
      const messages = data.messages || [];

      if (!messages.length) return;

      // Upsert all messages into the store
      dispatch(upsertMessages(messages));

      // Re-build thread summary from the full message set so messageIds is complete
      const existing = getState().mailThreads.byId[conversationId];
      if (!existing) return;

      const sorted = [...messages].sort((a, b) =>
        (a.receivedDateTime || '') < (b.receivedDateTime || '') ? -1 : 1
      );
      const latest   = sorted[sorted.length - 1];
      const unread   = sorted.filter(m => !m.isRead && !m.isDraft);
      const seen     = new Set();
      const participants = [];
      for (const m of sorted) {
        const name = m.from?.name || m.from?.email;
        if (name && !seen.has(name)) { seen.add(name); participants.push(name); }
      }

      dispatch(upsertThread({
        ...existing,
        messageIds:       sorted.map(m => m.id),
        messageCount:     sorted.length,
        hasUnread:        unread.length > 0,
        unreadCount:      unread.length,
        latestReceivedAt: latest.receivedDateTime || latest.sentDateTime || existing.latestReceivedAt,
        latestSenderId:   latest.from?.email      || existing.latestSenderId,
        latestSenderName: latest.from?.name       || latest.from?.email || existing.latestSenderName,
        participants,
      }));

    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ===========================================================================
// SEARCH
// ===========================================================================

/**
 * Search messages via Graph $search (KQL).
 *
 * The thunk exposes an AbortSignal via RTK's built-in abort mechanism.
 * The TopBar cancels the in-flight request before dispatching the next one:
 *   const promise = dispatch(searchMailThunk({ query }));
 *   promise.abort(); // cancels the axios request via AbortSignal
 *
 * Usage:
 *   dispatch(searchMailThunk({ query: 'invoice from:alice' }))
 */
export const searchMailThunk = createAsyncThunk(
  'graphMail/searchMail',
  async ({ query }, { dispatch, signal, rejectWithValue }) => {
    try {
      dispatch(commitSearchQuery(query));

      const data = await graph.searchMessages(query, { signal });

      const messages = data.messages || [];

      // Store messages in the normalized message cache so ReadingPane can
      // open them without a separate fetch.
      if (messages.length > 0) {
        dispatch(upsertMessages(messages));
      }

      dispatch(setSearchResults({
        messageIds: messages.map(m => m.id),
        threadIds:  [],
        total:      data.total ?? messages.length,
        nextCursor: data.cursor ?? null,
      }));

      if (query) dispatch(addRecentQuery(query));

      return { query, count: messages.length };
    } catch (err) {
      // RTK sets `aborted` when .abort() is called — don't treat it as an error
      if (err?.code === 'ERR_CANCELED' || signal.aborted) {
        return rejectWithValue({ canceled: true });
      }
      dispatch(setSearchError(err?.message || 'Search failed. Please try again.'));
      return rejectWithValue(err?.message);
    }
  }
);
